import { makeid } from 'utils/helpers/makeid';

import type { Prettify } from 'infrastructure/types';
import type { ExtractStepValues, TFormItem } from '../types';

export const monthlyTemplate_step_1 = [
  {
    key: makeid(10),
    title: <p>Recent Healthcare Activity</p>,
    type: 'radio',
    required: true,
    name: 'hasRecentHealthcareActivity',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'checkbox',
            name: 'recentHealthcareActivity',
            options: [
              {
                label: 'Provider appointment',
                value: 'providerAppointment',
                key: makeid(10),
              },
              {
                label: 'ER visit',
                value: 'erVisit',
                key: makeid(10),
              },
              {
                label: 'Inpatient stay',
                value: 'inpatientStay',
                key: makeid(10),
              },
              {
                label: 'Other',
                value: 'other',
                key: makeid(10),
                hideSubitemsIfParentNotSelected: true,
                subItems: [
                  {
                    key: makeid(10),
                    title: null,
                    type: 'input',
                    name: 'recentHealthcareActivityOtherText',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: makeid(10),
        label: 'No recent healthcare activity at this time',
        value: 'no',
      },
    ],
  },
] as const satisfies TFormItem[];

export const monthlyTemplate_step_2 = [
  {
    key: makeid(10),
    title: <p>Was Med Rec performed?</p>,
    type: 'radio',
    required: true,
    name: 'wasMedRecPerformed',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: <i>Timing:</i>,
            type: 'checkbox',
            name: 'timing',
            options: [
              {
                key: makeid(10),
                label: 'Routine',
                value: 'routine',
              },
              {
                key: makeid(10),
                label: 'In advance',
                value: 'inAdvance',
              },
              {
                key: makeid(10),
                label: 'After ER',
                value: 'afterEr',
              },
              {
                key: makeid(10),
                label: 'After hospital',
                value: 'afterHospital',
              },
            ],
          },
        ],
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Medication discrepancies?</p>,
    type: 'radio',
    required: true,
    name: 'medicationDiscrepancies',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            placeholder: 'Describe',
            type: 'textarea',
            name: 'medicationDiscrepanciesYesText',
          },
        ],
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Patient adherent?</p>,
    type: 'radio',
    required: true,
    name: 'patientAdherent',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            placeholder: 'Reason',
            type: 'textarea',
            name: 'patientAdherentNoText',
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Adherence/barriers discussed?</p>,
    type: 'radio',
    required: true,
    name: 'adherenceBarriersDiscussed',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: <i>Adherence/barriers indicated:</i>,
            type: 'radio',
            name: 'adherenceBarriersIndicated',
            options: [
              {
                key: makeid(10),
                label: 'Yes',
                value: 'yes',
                hideSubitemsIfParentNotSelected: true,
                subItems: [
                  {
                    key: makeid(10),
                    title: null,
                    type: 'input',
                    name: 'adherenceBarriersIndicatedYesText',
                  },
                ],
              },
              {
                key: makeid(10),
                label: 'None noted at this time',
                value: 'no',
              },
            ],
          },
        ],
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Changes/side-effects noted?</p>,
    type: 'radio',
    required: true,
    name: 'changesSideEffectsNoted',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            placeholder: 'Describe',
            type: 'textarea',
            name: 'changesSideEffectsNotedYesText',
          },
        ],
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
] as const satisfies TFormItem[];

export const monthlyTemplate_step_3 = [
  {
    key: makeid(10),
    title: <p>Did nurse update care plan?</p>,
    type: 'checkbox',
    required: true,
    name: 'didNurseUpdateCarePlan',
    options: [
      {
        label: 'New goals',
        value: 'nweGoals',
        key: makeid(10),
      },
      {
        label: 'Goal achieved',
        value: 'goalAchieved',
        key: makeid(10),
      },
      {
        label: 'Updated',
        value: 'updated',
        key: makeid(10),
      },
      {
        label: 'Reinforced',
        value: 'reinforced',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Goals</p>,
    description: 'select at least 1; add/modify as needed',
    type: 'checkbox',
    required: true,
    name: 'goals',
    options: [
      {
        key: makeid(10),
        label: 'Better control of (select all that apply)',
        value: 'betterControlOf',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'checkbox',
            required: false,
            name: 'betterControlOfList',
            options: [
              {
                key: makeid(10),
                label: 'Blood pressure',
                value: 'bloodPressure',
              },
              {
                key: makeid(10),
                label: 'Blood Sugar',
                value: 'bloodSugar',
              },
              {
                key: makeid(10),
                label: 'Pain',
                value: 'Pain',
              },
            ],
          },
        ],
      },
      {
        key: makeid(10),
        label: 'Maintain independence at home',
        value: 'maintainIndependence',
      },
      {
        key: makeid(10),
        label: 'Reduce hospital/ER visits',
        value: 'reduceHospitalERVisits',
      },
      {
        key: makeid(10),
        label: 'Improve mobility/function',
        value: 'improveMobilityFunction',
      },
      {
        key: makeid(10),
        label: 'Enhance mood/mental health',
        value: 'enhanceMoodMentalHealth',
      },
      {
        key: makeid(10),
        label: 'other',
        value: 'other',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            name: 'goalsOtherText',
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Measurable targets</p>,
    description: 'choose or enter per EHR logic/automation',
    type: 'radio',
    required: true,
    name: 'measurableTargets',
    options: [
      {
        label: 'BP < 140/90',
        value: 'bp14090',
        key: makeid(10),
      },
      {
        label: 'A1C < 8%',
        value: 'a1c8',
        key: makeid(10),
      },
      {
        label: 'other',
        value: 'other',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            name: 'measurableTargetsOtherText',
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Barriers</p>,
    description:
      'pick all that apply; can automate/populate per last visit, allow update',
    type: 'checkbox',
    required: true,
    name: 'barriers',
    options: [
      {
        label: 'Medication cost',
        value: 'medicationCost',
        key: makeid(10),
      },
      {
        label: 'Transportation',
        value: 'transportation',
        key: makeid(10),
      },
      {
        key: makeid(10),
        label: 'Forgetfulness',
        value: 'forgetfulness',
      },
      {
        key: makeid(10),
        label: 'Health literacy',
        value: 'healthLiteracy',
      },
      {
        key: makeid(10),
        label: 'Social isolation',
        value: 'socialIsolation',
      },
      {
        key: makeid(10),
        label: 'Pain/disability',
        value: 'painDisability',
      },
      {
        key: makeid(10),
        label: 'Financial strain',
        value: 'financialStrain',
      },
      {
        key: makeid(10),
        label: 'No support at home',
        value: 'noSupportAtHome',
      },
      {
        key: makeid(10),
        label: 'Behavioral health/depression',
        value: 'behavioralHealthDepression',
      },
      {
        key: makeid(10),
        label: 'Nonadherence',
        value: 'nonadherence',
      },
      {
        key: makeid(10),
        label: 'other',
        value: 'other',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            name: 'barriersOtherText',
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Date care plan last updated</p>,
    type: 'input-date',
    required: true,
    maxDate: new Date(),
    name: 'dateCarePlanLastUpdated',
  },
  {
    key: makeid(10),
    title: <p>Care plan reviewed at this encounter? </p>,
    type: 'radio',
    required: true,
    name: 'carePlanReviewedAtThisEncounter',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
] as const satisfies TFormItem[];

export const monthlyTemplate_step_4 = [
  {
    key: makeid(10),
    title: <p>ADL/functional status checked?</p>,
    type: 'radio',
    name: 'adlFunctionalStatusChecked',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Cognitive status screened?</p>,
    type: 'radio',
    name: 'cognitiveStatusScreened',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
      {
        key: makeid(10),
        label: 'Not indicated  ',
        value: 'notIndicated',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Behavioral health screening (PHQ-2/9)</p>,
    type: 'radio',
    name: 'behavioralHealthScreening',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Fall/safety risk assessed?</p>,
    type: 'radio',
    name: 'fallSafetyRiskAssessed',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Escalations/red flags today?</p>,
    type: 'radio',
    name: 'escalationsRedFlagsToday',
    options: [
      {
        key: makeid(10),
        label: 'Yes',
        value: 'yes',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            placeholder: 'Describe',
            type: 'textarea',
            name: 'escalationsRedFlagsTodayYesText',
          },
        ],
      },
      {
        key: makeid(10),
        label: 'No',
        value: 'no',
      },
    ],
  },
] as const satisfies TFormItem[];

export const monthlyTemplate_step_5 = [
  {
    key: makeid(10),
    title: <p>SDOH/needs assessment updated?</p>,
    type: 'radio',
    required: true,
    name: 'sdohNeedsAssessmentUpdated',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Any unmet needs or referrals this month? </p>,
    type: 'radio',
    required: true,
    name: 'anyUnmetNeedsOrReferralsThisMonth',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'textarea',
            required: false,
            placeholder: 'Reason',
            name: 'anyUnmetNeedsOrReferralsThisMonthYesText',
          },
        ],
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
] as const satisfies TFormItem[];

export const monthlyTemplate_step_6 = [
  {
    key: makeid(10),
    title: <p>Preventive care reviewed? </p>,
    description: 'AWV, A1C, vaccines, cancer screen, etc.',
    type: 'radio',
    required: true,
    name: 'preventiveCareReviewed',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Outstanding items/next due</p>,
    type: 'textarea',
    required: true,
    name: 'outstandingItemsNextDue',
  },
] as const satisfies TFormItem[];

export const monthlyTemplate_step_7 = [
  {
    key: makeid(10),
    title: (
      <p>
        Coordination with providers/pharmacies/community agencies as needed?
      </p>
    ),
    type: 'radio',
    required: true,
    name: 'coordinationWithProviders',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Any referrals/follow-ups documented this month?</p>,
    type: 'radio',
    required: true,
    name: 'anyReferralsFollowUps',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'textarea',
            placeholder: 'Describe',
            name: 'anyReferralsFollowUpsYesText',
          },
        ],
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
] as const satisfies TFormItem[];

export type Step_1_Values = ExtractStepValues<typeof monthlyTemplate_step_1>;
export type Step_2_Values = ExtractStepValues<typeof monthlyTemplate_step_2>;
export type Step_3_Values = ExtractStepValues<typeof monthlyTemplate_step_3>;
export type Step_4_Values = ExtractStepValues<typeof monthlyTemplate_step_4>;
export type Step_5_Values = ExtractStepValues<typeof monthlyTemplate_step_5>;
export type Step_6_Values = ExtractStepValues<typeof monthlyTemplate_step_6>;
export type Step_7_Values = ExtractStepValues<typeof monthlyTemplate_step_7>;

export type MonthlySteppedValues = {
  STEP1: Prettify<Partial<Step_1_Values>>;
  STEP2: Prettify<Partial<Step_2_Values>>;
  STEP3: Prettify<Partial<Step_3_Values>>;
  STEP4: Prettify<Partial<Step_4_Values>>;
  STEP5: Prettify<Partial<Step_5_Values>>;
  STEP6: Prettify<Partial<Step_6_Values>>;
  STEP7: Prettify<Partial<Step_7_Values>>;
};

type StepType = { title: string; template: TFormItem[] };

export const steppedTemplates = {
  0: {
    title: 'Recent Healthcare Activity',
    template: monthlyTemplate_step_1,
  },
  1: {
    title: 'Medication Reconciliation Review',
    template: monthlyTemplate_step_2,
  },
  2: {
    title: 'Care Plan Review and Update',
    template: monthlyTemplate_step_3,
  },
  3: {
    title: 'Functional, Cognitive, Behavioral & Safety Assessments',
    template: monthlyTemplate_step_4,
  },
  4: {
    title: 'Social Determinants of Health (SDOH)/Social Needs',
    template: monthlyTemplate_step_5,
  },
  5: {
    title: 'Quality Gaps & Preventive Services',
    template: monthlyTemplate_step_6,
  },
  6: {
    title: 'Care Coordination/Referrals',
    template: monthlyTemplate_step_7,
  },
} as const satisfies Record<number, StepType>;

export type TMonthlyTemplateValues = Partial<
  Step_1_Values &
    Step_2_Values &
    Step_3_Values &
    Step_4_Values &
    Step_5_Values &
    Step_6_Values &
    Step_7_Values
>;
